import { Box, Stack, Typography } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-premium";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { TruncateTypography } from "components/TruncateTypography";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  WasteLinesUnifiedQuery,
  WasteLinesUnifiedQueryVariables,
} from "generated-graphql/graphql";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { formatNumber } from "util/formatNumber";
import { WASTE_LINES_UNIFIED_QUERY } from "../../../ManifestLineItems/Preview/api";
import { WasteTagsColumnHeader } from "components/WasteTagsColumnHeader";
import { formatShipmentRangeDate } from "../formatShipmentRangeDate";
import { Filter } from "../Preview/WasteFacilityFilters";
import { useWasteFacility } from "../useWasteFacility";
import { TileContentGreyBackground } from "./TileContentGreyBackground";
import { useFacilityWasteLineItemsDefaultSearch } from "./useFacilityWasteLineItemsDefaultSearch";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { useWasteFacilityState } from "./WasteFacilityContext";

type ManifestLineItemTileRow = NonNullable<
  NonNullable<WasteLinesUnifiedQuery["wasteLinesUnified"]>["items"]
>[number];

export const ManifestLineItemsTile = () => {
  return (
    <DashboardTile size={12}>
      <ManifestLineItemsTileContent />
    </DashboardTile>
  );
};

const ManifestLineItemsTileContent = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { tenantId } = useTenant();
  const { pushIdToUrl } = useSelectedIdInUrl("wasteLineUnifiedId", {
    prefixSegment: "manifest-line-items",
  });
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { data } = useWasteFacility(facilityId);

  const { startDate } = deferredFilters as Filter;

  const exportFilename = useMemo(
    () =>
      data.facility?.name
        ? `${data.facility.name}-manifest-line-items`
        : undefined,
    [data.facility?.name]
  );

  const defaultSearch = useFacilityWasteLineItemsDefaultSearch();
  const hasShipmentData = useHasWasteShipmentData();

  const formattedDateRange = useMemo(() => {
    return formatShipmentRangeDate({
      startDate: deferredFilters.startDate,
      endDate: deferredFilters.endDate,
    });
  }, [deferredFilters.startDate, deferredFilters.endDate]);

  return (
    <>
      <DashboardTile.Header
        title="Manifest Line Items"
        infoTooltip="See manifest line items for this facility in the date range. If line item is present in both vendor and e‑Manifest data, we display the vendor data unless otherwise noted."
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="textSecondary">
            {formattedDateRange}
          </Typography>
        </Stack>
      </DashboardTile.Header>
      <DashboardTile.ContentArea>
        {!hasShipmentData ? (
          <TileContentGreyBackground>
            🧾 No line item data to show
          </TileContentGreyBackground>
        ) : (
          <OmnisearchDataGrid<
            WasteLinesUnifiedQuery,
            WasteLinesUnifiedQueryVariables,
            ManifestLineItemTileRow
          >
            persistenceKey="facility-manifest-line-items-table"
            isURLDriven={false}
            columns={[
              {
                field: "lineItem",
                headerName: "Line Item",
                minWidth: 100,
                flex: 2,
                renderCell: ({ row: { willNotReport }, value }) => {
                  return (
                    <Box>
                      <TruncateTypography variant="body2">
                        {value}
                      </TruncateTypography>
                      {willNotReport && (
                        <Typography variant="caption" color="text.secondary">
                          Not Reportable
                        </Typography>
                      )}
                    </Box>
                  );
                },
              },
              {
                field: "profileNumber",
                headerName: "Profile #",
                flex: 0.5,
                renderCell: ({ value }) => {
                  return (
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <TruncateTypography variant="body2">
                        {value}
                      </TruncateTypography>
                    </Box>
                  );
                },
              },
              {
                field: "manifestTrackingNumber",
                headerName: "Manifest #",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "vendorAmount",
                filterKeyType: "number",
                headerName: "Amount (Vendor)",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  const amount = formatNumber(row.vendorAmount);
                  return amount ? `${amount} lbs` : "";
                },
              },
              {
                field: "eManifestAmount",
                filterKeyType: "number",
                headerName: "Amount (e‑Manifest)",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  const amount = formatNumber(row.eManifestAmount);
                  return amount ? `${amount} lbs` : "";
                },
              },
              {
                field: "shippedDate",
                filterable: false,
                headerName: "Ship Date",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                valueGetter: (_, row) => {
                  return getUtcDisplayDate(row.shippedDate, "med");
                },
              },
              {
                field: "dotDescription",
                headerName: "DOT Description",
                flex: 1,
                minWidth: 150,
                headerClassName: "wrapHeader",
              },
              {
                field: "managementMethodCode",
                headerName: "Management Method Code",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "vendor",
                headerName: "Vendor",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "destinationFacilityName",
                headerName: "TSDF",
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "tags",
                renderHeader: () => <WasteTagsColumnHeader />,
                flex: 0.5,
                minWidth: 100,
                maxWidth: 150,
                renderCell: ({ row: { tags } }) => {
                  return <TagsDisplay tags={tags} />;
                },
              },
            ]}
            dataQuery={WASTE_LINES_UNIFIED_QUERY}
            getItems={(data) => data.wasteLinesUnified?.items ?? []}
            getCount={(data) => data.wasteLinesUnified?.count ?? 0}
            initialPageSize={10}
            withPadding={false}
            defaultSearch={defaultSearch}
            noDataMessage={"No manifest line items found for this facility."}
            initialSortModel={[{ field: "shippedDate", sort: "desc" }]}
            onRowClick={(params: GridRowParams<ManifestLineItemTileRow>) =>
              pushIdToUrl(params.row.id)
            }
            isRowSelectable={() => false}
            disableRowSelectionOnClick={true}
            initialColumnVisibilityModel={{
              dotDescription: false,
            }}
            commandButtons={({ search }) => [
              <ExportDataButton
                timeoutInSeconds={180}
                key="export"
                iconOnly={true}
                exportType="wasteManifestLines"
                disabled={!facilityId || !startDate}
                tenantId={tenantId ?? ""}
                fileName={exportFilename}
                filter={{ search }}
              />,
            ]}
          />
        )}
      </DashboardTile.ContentArea>
    </>
  );
};
